import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
} from "react";
import { useSearchParams } from "react-router";
import { useDistricts } from "@/api/districts";

type DistrictContextType = {
	districtId: number | null;
	districtName: string | null;
	setDistrict: (name: string | null) => void;
};

const DistrictContext = createContext<DistrictContextType | null>(null);

const STORAGE_KEY = "metriso.district";

function readStoredDistrict(): string | null {
	if (typeof window === "undefined") return null;
	try {
		return localStorage.getItem(STORAGE_KEY);
	} catch {
		return null;
	}
}

export function DistrictProvider({ children }: { children: React.ReactNode }) {
	const [searchParams, setSearchParams] = useSearchParams();
	const { data: districts } = useDistricts();

	const districtName = useMemo(() => {
		const fromUrl = searchParams.get("osiedle");
		if (fromUrl) return fromUrl;
		return readStoredDistrict();
	}, [searchParams]);

	const districtId = useMemo(() => {
		if (!districtName || !districts) return null;
		return districts.find((d) => d.name === districtName)?.id ?? null;
	}, [districtName, districts]);

	const setDistrict = useCallback(
		(name: string | null) => {
			try {
				if (name === null) localStorage.removeItem(STORAGE_KEY);
				else localStorage.setItem(STORAGE_KEY, name);
			} catch {
				// ignore storage errors (private mode, quota, etc.)
			}
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);
					if (name === null) {
						next.delete("osiedle");
					} else {
						next.set("osiedle", name);
					}
					return next;
				},
				{ replace: true },
			);
		},
		[setSearchParams],
	);

	useEffect(() => {
		if (!districts || districts.length === 0) return;
		const fromUrl = searchParams.get("osiedle");
		if (fromUrl) {
			if (!districts.some((d) => d.name === fromUrl)) setDistrict(null);
			return;
		}
		const fromStorage = readStoredDistrict();
		if (fromStorage && districts.some((d) => d.name === fromStorage)) {
			setDistrict(fromStorage);
		}
	}, [districts, searchParams, setDistrict]);

	return (
		<DistrictContext.Provider value={{ districtId, districtName, setDistrict }}>
			{children}
		</DistrictContext.Provider>
	);
}

export function useDistrict() {
	const context = useContext(DistrictContext);
	if (!context) {
		throw new Error("useDistrict must be used within a DistrictProvider");
	}
	return context;
}
